import { Button } from '../../components/Button';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { StickyActionBar } from '../../components/StickyActionBar';
import { formatCurrency } from '../../utils/formatCurrency';
import { ActionBar } from '../ActionBar';
import { BookingNotesCard } from '../BookingNotesCard';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import {
  ActionBarArea,
  AddGuestRow,
  FooterLeft,
  FooterRow,
  FooterTitle,
  FooterTotal,
  FormGroup,
  Heading,
  HeadingAccent,
  Inner,
  ScrollArea,
  Wrapper,
} from './GuestDetailsContentPanel.styles';
import type { GuestDetailsContentPanelProps } from './GuestDetailsContentPanel.types';

export function GuestDetailsContentPanel({
  guests,
  onGuestChange,
  onExistingGuestSelect,
  existingGuests,
  titleOptions,
  genderOptions,
  maxGuests = 2,
  onAddGuest,
  onRemoveGuest,
  showNotes = true,
  notesChecked = false,
  onNotesToggle,
  notesValue = '',
  onNotesChange,
  onBack,
  currentStep = 1,
  totalSteps,
  footerTitle,
  totalAmount,
  currencyCode,
  locale = 'en-GB',
  onNext,
  nextDisabled = false,
  isLoading = false,
  labels = {},
}: GuestDetailsContentPanelProps) {
  const {
    title = 'Guest details.',
    titleAccent = 'Just a name to hold your room.',
    backAriaLabel = 'Go back',
    progressLabel = 'Booking progress',
    guestTitleFormatter,
    guestSubtitle,
    dividerText,
    titleLabel,
    titlePlaceholder,
    firstNameLabel,
    lastNameLabel,
    addGuest = 'Add guest',
    removeGuest = 'Remove',
    notesTitle,
    notesPlaceholder,
    totalLabel = 'Total',
    nextLabel = 'Next',
  } = labels;

  const canAddGuest = !!onAddGuest && guests.length < maxGuests;

  const selectedIdsByIndex = guests.map((guest) => guest.selectedExistingGuestId);

  return (
    <Wrapper aria-busy={isLoading || undefined}>
      <ScrollArea>
        <Inner>
          <ActionBar
            onBack={onBack}
            backAriaLabel={backAriaLabel}
            currentStep={currentStep}
            totalSteps={totalSteps}
            progressLabel={progressLabel}
          />
          {isLoading ? (
            <>
              <Heading>
                <SkeletonBlock variant="heading" />
              </Heading>
              <FormGroup>
                <SkeletonBlock height="56px" borderRadius="12px" />
                <SkeletonBlock height="56px" borderRadius="12px" />
                <SkeletonBlock height="56px" borderRadius="12px" />
                <SkeletonBlock height="120px" borderRadius="12px" />
              </FormGroup>
            </>
          ) : (
            <>
              <Heading>
                {title} <HeadingAccent>{titleAccent}</HeadingAccent>
              </Heading>
              <FormGroup>
                {guests.map((guest, index) => (
                  <GuestDetailsPanel
                    key={index}
                    guestNumber={index + 1}
                    showHeader={index > 0}
                    subtitle={guestSubtitle}
                    onRemove={index > 0 && onRemoveGuest ? () => onRemoveGuest(index) : undefined}
                    existingGuests={existingGuests}
                    selectedGuestId={guest.selectedExistingGuestId}
                    disabledGuestIds={selectedIdsByIndex.filter(
                      (id, otherIndex): id is string => !!id && otherIndex !== index
                    )}
                    onExistingGuestSelect={(guestId) => onExistingGuestSelect?.(index, guestId)}
                    values={guest.values}
                    errors={guest.errors}
                    onChange={(field, value) => onGuestChange(index, field, value)}
                    titleOptions={titleOptions}
                    genderOptions={genderOptions}
                    showGender={false}
                    dividerText={dividerText}
                    requiredFields={
                      index === 0 ? { title: true, firstName: true, lastName: true } : {}
                    }
                    labels={{
                      guestTitleFormatter,
                      titleLabel,
                      titlePlaceholder,
                      firstNameLabel,
                      lastNameLabel,
                      removeLabel: removeGuest,
                    }}
                  />
                ))}
                {canAddGuest && (
                  <AddGuestRow>
                    <Button type="button" variant="outline" size="small" onClick={onAddGuest}>
                      {addGuest}
                    </Button>
                  </AddGuestRow>
                )}
                {showNotes && (
                  <BookingNotesCard
                    checked={notesChecked}
                    onToggle={(checked) => onNotesToggle?.(checked)}
                    value={notesValue}
                    onChange={(value) => onNotesChange?.(value)}
                    labels={{ title: notesTitle, placeholder: notesPlaceholder }}
                  />
                )}
              </FormGroup>
            </>
          )}
        </Inner>
      </ScrollArea>
      <ActionBarArea>
        <StickyActionBar>
          <FooterRow>
            <FooterLeft>
              {isLoading ? (
                <>
                  <SkeletonBlock width="140px" height="16px" />
                  <SkeletonBlock width="90px" height="20px" />
                </>
              ) : (
                <>
                  {footerTitle && <FooterTitle>{footerTitle}</FooterTitle>}
                  <FooterTotal>
                    {totalLabel} {formatCurrency(totalAmount, currencyCode, { locale })}
                  </FooterTotal>
                </>
              )}
            </FooterLeft>
            {isLoading ? (
              <SkeletonBlock width="96px" height="48px" borderRadius="24px" />
            ) : (
              <Button type="button" variant="primary" onClick={onNext} disabled={nextDisabled}>
                {nextLabel}
              </Button>
            )}
          </FooterRow>
        </StickyActionBar>
      </ActionBarArea>
    </Wrapper>
  );
}
